import { ExclamationTriangleIcon, HomeIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect } from "react";
import { useRouteError, useNavigate } from "react-router-dom";

import { captureException } from "@/shared/lib/sentry";

import { Button } from "./button";
import { useRouteErrorMessages } from "../hooks/useRouteErrorMessages";

export function RouteErrorElement() {
  const error = useRouteError();
  const navigate = useNavigate();
  const msg = useRouteErrorMessages();

  // Capture route errors in Sentry
  useEffect(() => {
    if (error) {
      captureException(error, { source: "RouteErrorElement" });
    }
  }, [error]);

  // Handle different types of router errors
  const getErrorMessage = (): React.ReactNode => {
    if (error && typeof error === "object") {
      // Handle network errors and module loading failures
      if ("message" in error && typeof error.message === "string") {
        if (error.message.includes("Loading chunk") || error.message.includes("Importing a module")) {
          return msg.isDevelopment ? msg.moduleLoadDev : msg.moduleLoadProd;
        }
      }

      // Handle HTTP errors
      if ("status" in error && "statusText" in error) {
        const status = error.status as number;
        const statusText = error.statusText as string;

        switch (status) {
          case 404:
            return msg.notFound;
          case 403:
            return msg.forbidden;
          case 500:
            return msg.isDevelopment
              ? msg.serverErrorDev(statusText)
              : msg.serverErrorProd;
          default:
            return msg.isDevelopment
              ? msg.genericDev(status, statusText)
              : msg.genericProd;
        }
      }

      // Generic error object
      if ("message" in error) {
        return msg.isDevelopment ? String(error.message) : msg.unknown;
      }
    }

    return msg.isDevelopment
      ? msg.unknownDetailsDev(String(error))
      : msg.genericProd;
  };

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const handleGoHome = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {msg.isDevelopment ? msg.pageErrorDev : msg.pageErrorProd}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {getErrorMessage()}
                </p>
              </div>

              {msg.isDevelopment && error ? (
                <div className="mt-4 p-4 bg-yellow-50 rounded-md text-left">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">{msg.errorDetails}</h4>
                  <pre className="text-xs text-yellow-700 whitespace-pre-wrap break-words">
                    {String(JSON.stringify(error ?? {}, null, 2))}
                  </pre>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-5 sm:mt-6 space-y-3">
            <Button
              type="button"
              className="w-full"
              onClick={handleRetry}
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              {msg.reloadPage}
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleGoBack}
              >
                {msg.goBack}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoHome}
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                {msg.home}
              </Button>
            </div>

            {!msg.isDevelopment && (
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  {msg.contactSupport}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}